import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined'
import Badge from '@mui/material/Badge'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { grey } from '@mui/material/colors'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import Menu from '@mui/material/Menu'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import SvgIcon from '@mui/material/SvgIcon'
import Typography from '@mui/material/Typography'
import { Bell } from '@styled-icons/heroicons-outline'
import React, { useCallback, useState } from 'react'
import { Link } from 'react-router-dom-v5-compat'

import MenuToolTip from 'components/common/Tooltip'
import routes from 'router/routes'
import T from 'T'
import { Z_INDEX } from 'theme/zIndex'

import NotificationBellPreviewItem from './NotificationBellPreviewItem'
import { NOTIFICATION_PANEL_WIDTH } from './notificationCenterConstants'
import NotificationEmptyState from './NotificationEmptyState'
import { useNotificationReadState } from './useNotificationReadState'
import { useNotificationsSubscription } from './useNotificationsSubscription'

const { MENU } = Z_INDEX

const PREVIEW_LIMIT = 5

// Badge and list come from one subscription, so a run arriving while the tab is open lands in both.
const NotificationBellMenu = () => {
  const theme = useTheme()
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null)
  const { markEverythingRead, isMarkingEverythingRead } = useNotificationReadState()

  const { unreadCount, latestUnread } = useNotificationsSubscription()

  const isMenuOpen = Boolean(menuAnchorEl)
  const unread = latestUnread.slice(0, PREVIEW_LIMIT)

  const handleOpen = useCallback((event: React.MouseEvent<HTMLElement>) => setMenuAnchorEl(event.currentTarget), [])

  const handleClose = useCallback(() => setMenuAnchorEl(null), [])

  // Following a notification is not answering it, so the read state stays the reader's own to set.
  const handleSelect = useCallback(() => setMenuAnchorEl(null), [])

  return (
    <>
      <MenuToolTip title={T.NOTIFICATIONS}>
        <IconButton size="large" onClick={handleOpen}>
          <Badge badgeContent={unreadCount} color="primary" max={99}>
            <SvgIcon sx={{ color: isMenuOpen ? theme.palette.background.paper : grey[500] }}>
              <Bell />
            </SvgIcon>
          </Badge>
        </IconButton>
      </MenuToolTip>

      <Menu
        anchorEl={menuAnchorEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={isMenuOpen}
        sx={{ zIndex: MENU, '.MuiMenu-list': { minWidth: NOTIFICATION_PANEL_WIDTH, maxWidth: NOTIFICATION_PANEL_WIDTH, py: 0 } }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClose={handleClose}
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          {/* h5 is the scale's 16px step and already carries the 600 weight, so no override is needed. */}
          <Typography variant="h5">{T.NOTIFICATIONS}</Typography>
          <Button
            disabled={unreadCount === 0}
            loading={isMarkingEverythingRead}
            size="small"
            startIcon={<DoneAllOutlinedIcon />}
            onClick={markEverythingRead}
          >
            {T.MARK_ALL_AS_READ}
          </Button>
        </Stack>
        {/* On the divider rather than replacing it, so the rows below don't shift as the bar appears. */}
        <Box sx={{ position: 'relative' }}>
          <Divider />
          {isMarkingEverythingRead && <LinearProgress sx={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} />}
        </Box>

        {unread.length === 0 && (
          <NotificationEmptyState description={T.NOTIFICATIONS_EMPTY_STATE_DESCRIPTION} title={T.NOTIFICATIONS_EMPTY_STATE_TITLE} />
        )}

        {unread.map((notification, index) => (
          <NotificationBellPreviewItem
            key={notification.id}
            divider={index < unread.length - 1}
            notification={notification}
            onSelect={handleSelect}
          />
        ))}

        <Divider />
        <Box sx={{ p: 1 }}>
          <Button fullWidth component={Link} size="small" to={routes.app.notifications} onClick={handleClose}>
            {T.VIEW_ALL_NOTIFICATIONS}
          </Button>
        </Box>
      </Menu>
    </>
  )
}

export default NotificationBellMenu
